import React, { useCallback, useEffect, useRef, useState } from "react";
import ct from "class-types.macro";
import smoothscroll from "smoothscroll-polyfill";
import { BrandSummary } from "~/common/Store/models/Brand";
import BrandListingItem from "./BrandListingItem";
import BrandCarousel from "./BrandCarousel";
import { CategoryEnhancedGridSectionResult } from "~/common/Store/models/Category";
import brandSection from "./brandSection";
import "./brandGrid.scss";

interface Props {
  brands: BrandSummary[];
  mainHeading?: string;
  secondaryHeading?: string;
  brandSection: CategoryEnhancedGridSectionResult[];
}

interface FilteredResults {
  record: BrandSummary[];
  letter: string;
}

interface GroupedBrands {
  record: BrandSummary[];
  letter: string;
}

const BrandListing = ({ brands, mainHeading, secondaryHeading }: Props) => {
  const isNumeric = (num: string) => {
    return !Number.isNaN(Number(num));
  };

  const [groupedBrands] = useState<GroupedBrands[]>(() => {
    const brand = brands.reduce(
      (previousValue, currentValue) => {
        const letter = currentValue.name[0]?.toUpperCase();
        const prev = previousValue;
        const curr = currentValue;

        if (letter !== undefined) {
          if (isNumeric(letter)) {
            if (!prev["#"]) {
              prev["#"] = { letter: "#", record: [curr] };
            } else {
              prev["#"]?.record.push(curr);
            }
          } else if (!prev[letter]) {
            prev[letter] = { letter, record: [curr] };
          } else {
            prev[letter]?.record.push(curr);
          }
        }
        return prev;
      },
      {} as { [key: string]: { letter: string; record: BrandSummary[] } },
    );
    return Object.values(brand);
  });

  const refs = useRef(
    Array.from({ length: Object.values(groupedBrands).length }, () =>
      React.createRef<HTMLDivElement>(),
    ),
  );

  const [filterBrands, setFilterBrands] = useState<FilteredResults[]>([]);
  const [isVisible, setIsVisible] = useState<boolean | undefined>(false);
  const [searchWord, setSearchWord] = useState("");
  const lazyLoadBrandImageAfterCount = 30;
  const [firstHalfBrands, secondHalfBrands] = [
    filterBrands.slice(0, 14),
    filterBrands.slice(14),
  ];

  const vanillaLazyLoadImages = async () => {
    const { default: LazyLoad } = await import("vanilla-lazyload");
    return new LazyLoad({
      elements_selector: ".llvendor",
      use_native: true,
    });
  };

  useEffect(() => {
    smoothscroll.polyfill();
    vanillaLazyLoadImages();
  }, []);

  const headerText = mainHeading || "Product Vendors";
  const secondaryText = secondaryHeading || "Featured Brands";

  const filteredBrands = useCallback(() => {
    const results = groupedBrands.map((item) => ({
      ...item,
      record: item.record.filter((i) => {
        return i.name.toLowerCase().startsWith(searchWord.toLowerCase());
      }),
    }));
    setFilterBrands(results);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchWord]);

  useEffect(() => {
    const element = document.getElementById("A") as Element;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry && entry?.boundingClientRect?.top > 0) {
          setIsVisible(false);
        } else {
          setIsVisible(true);
        }
      },
      {
        root: null,
        rootMargin: undefined,
        threshold: 1,
      },
    );

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [filterBrands]);

  useEffect(() => {
    filteredBrands();
  }, [filteredBrands]);

  const noResults = () => {
    return filterBrands
      .map((brand) => {
        if (brand.record.length === 0) {
          return true;
        }
        return false;
      })
      .every((result) => result === true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setSearchWord(e.target.value);
  };

  const cancelSearch = () => {
    setSearchWord("");
  };

  return (
    <div className="container">
      <div
        className={ct(
          "flex",
          "items-center",
          "justify-between",
          "mb-4",
          "mt-8",
        )}
      >
        <h3 className={ct("text-3xl", "mb-0")}>{secondaryText}</h3>
        <a className="btn btn-small btn-info" href="/ourbrands.html">
          View Our Brands
        </a>
      </div>
      <BrandCarousel brandSection={brandSection} />
      <div className={ct("flex", "items-center", "justify-between", "mt-8")}>
        <div>
          <h1 className={ct("text-3xl", "mb-0")}>{headerText}</h1>
          <p className={ct("text-sm", "leading-6", "mb-0")}>
            WebstaurantStore is proud to carry the most trusted restaurant
            supply brands in the industry
          </p>
        </div>
        <div className={ct("hidden", "md:block")}>
          <a
            href="/vendor-application/"
            target="_blank"
            className="header__promo-block"
            title="Apply to be a Vendor with Us"
          >
            <span className="header__promo-image header__promo-image--no-border">
              <img
                className="promo-image--sm"
                src="//cdnimg.webstaurantstore.com/uploads/design/2019/11/va-store-icon.png"
                alt="Illustration of a Store Front"
              />
            </span>
            <span className="header__promo-text">
              <span className="header__promo-title">Want to be a Vendor?</span>
              <span className="header__promo-action">
                Apply to Sell Products <i className="icon-breadcrumb" />
              </span>
            </span>
          </a>
        </div>
      </div>
      <div id="page">
        <div id="main" className="grid-layout" role="main">
          <div className="brand-grid-container grid-module-large">
            <div
              style={{ marginRight: "0px" }}
              className="px-10 py-10 bg-white border border-solid border-gray-200 rounded-md relative"
            >
              <div
                className={`transition transition-opacity duration-100 ease-in-out  absolute opacity-0 flex flex-row-reverse box-border flex-wrap-reverse right-10 ${
                  searchWord === "" && isVisible ? "opacity-100" : ""
                }`}
              >
                <button
                  type="button"
                  onClick={() => {
                    if (window) {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }
                    setIsVisible(false);
                  }}
                  className="fixed bottom-10 bg-green-400 border-none flex justify-center items-center rounded-full h-16 w-16 shadow-md z-30"
                >
                  <svg
                    width="23"
                    height="15"
                    viewBox="0 0 23 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="11.3137"
                      y="6.10352e-05"
                      width="4"
                      height="16"
                      transform="rotate(45 11.3137 6.10352e-05)"
                      fill="white"
                    />
                    <rect
                      x="8.24292"
                      y="2.82843"
                      width="4"
                      height="16"
                      transform="rotate(-45 8.24292 2.82843)"
                      fill="white"
                    />
                  </svg>
                </button>
              </div>
              <div className="flex flex-col mb-8">
                <label
                  htmlFor="searchBrands"
                  className="box-border inline-block m-0"
                >
                  Search
                </label>
                <div className="flex flex-col xl:flex-row xl:items-center">
                  <input
                    id="searchBrands"
                    onChange={handleChange}
                    value={searchWord}
                    placeholder="Brand Name"
                    className="box-border inline-block border border-solid border-gray-200 p-2 w-full xl:w-64 self-start md:mb-4 xl:mb-0 rounded-sm shadow-inner"
                  />
                  <div className="hidden md:flex flex-col items-center xl:items-start gap-y-2 flex-wrap xl:flex-row xxl:items-start w-full lg:ml-4">
                    <div className="flex xl:w-1/2">
                      {firstHalfBrands.map((brand, index) => (
                        <button
                          key={`${brand.letter}${index}`}
                          type="button"
                          onClick={() => {
                            refs.current[index]?.current?.scrollIntoView({
                              behavior: "smooth",
                            });
                            if (index > 1) {
                              setIsVisible(true);
                            }
                          }}
                          className="text-center first:ml-0 mx-1 bg-gray-200 rounded-sm text-black border-none h-9 text-base px-4 xl:text-sm xl:p-2 xl:w-8 flex-grow xl:flex-shrink-1"
                        >
                          {brand.letter}
                        </button>
                      ))}
                    </div>
                    <div className="flex xl:w-1/2">
                      {secondHalfBrands.map((brand, index) => {
                        const indexOfBrand = filterBrands.findIndex(
                          (b) => b.letter === brand.letter,
                        );
                        return (
                          <button
                            key={`${brand.letter}${index}`}
                            type="button"
                            onClick={() => {
                              refs.current[
                                indexOfBrand
                              ]?.current?.scrollIntoView({
                                behavior: "smooth",
                              });
                              setIsVisible(true);
                            }}
                            className="text-center last:mr-0 mx-1 bg-gray-200 rounded-sm text-black border-none h-9 text-base px-4 xl:text-sm xl:p-2 xl:w-8 flex-grow xl:flex-shrink-1"
                          >
                            {brand.letter}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {noResults() && searchWord !== "" ? (
                <div className="flex flex-col justify-center items-center text-center py-12">
                  <p className="font-bold mb-0">No Results Found</p>
                  <p>
                    Try adjusting you search to find what you&apos;re looking
                    for.
                  </p>
                  <button
                    type="button"
                    className="text-red border-none bg-white flex items-center"
                    onClick={cancelSearch}
                  >
                    <svg
                      className="pr-1"
                      width="11"
                      height="12"
                      viewBox="0 0 11 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="10.4391"
                        y="9.59802"
                        width="2"
                        height="12"
                        transform="rotate(135 10.4391 9.59802)"
                        fill="#AE1D1D"
                      />
                      <rect
                        x="1.97516"
                        y="10.887"
                        width="2"
                        height="12"
                        transform="rotate(-135 1.97516 10.887)"
                        fill="#AE1D1D"
                      />
                    </svg>
                    clear search results
                  </button>
                </div>
              ) : (
                <div>
                  {filterBrands.map((brand, index) => (
                    <div
                      key={`${brand.letter}${index}`}
                      className={`mb-8 flex flex-col md:flex-row md:items-start ${
                        searchWord !== "" && brand.record.length === 0
                          ? "hidden"
                          : ""
                      }`}
                    >
                      <div
                        ref={refs.current[index]}
                        id={brand.letter}
                        className={`text-3xl mb-3 md:mb-0 md:pr-3 md:w-12 text-left ${
                          searchWord.length > 0 ? "hidden" : "flex"
                        }`}
                      >
                        {brand.letter}
                      </div>
                      <div className="grid-children grid grid-cols-3 md:grid-cols-4 box-border gap-4">
                        {brand.record.map((record) => (
                          <BrandListingItem
                            key={record.name}
                            name={record.name}
                            image={record.image}
                            link={record.link}
                            lazyLoadImage={
                              index >= lazyLoadBrandImageAfterCount
                            }
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandListing;
